import { h, Component, Host, State, Element, Prop, Watch, Listen, EventEmitter, Event } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  CardTeaserSchemeDefinition,
  CardTeaserSchemeName,
  CardTeaserTextAlignment,
  FlexHeadlineColor
} from "./constants/card-teaser-types";
import { CARD_TEASER_SCHEME_CONFIG, CARD_TEASER_SCHEME_MAP } from "./constants/card-teaser-constants";
import { getFirstNestedChild, getNestedChildren } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
// import { isMobile } from "../../utils/screen-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { HEADLINE_SIZE_H5, HEADLINE_TAG_H5 } from "../headline/constants/headline-constants";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import { CardTeaserSchemeConfiguration } from "../../constants/colors";
import {
  CARD_TEASER_DEFAULT_PROMO_FLAG_ALIGNMENT,
  CARD_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  CARD_TEASER_DEFAULT_VIOLATOR_VERTICAL_POSITION,
  CARD_TEASER_TEXT_POSITION,
  CardTeaserPromoFlagAlignment,
  CardTeaserViolatorHorizontalPosition,
  CardTeaserViolatorVerticalPosition
} from "../../constants/global-constants";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";

/**
 * @slot [unnamed] - Holds either a single eon-ui-link or an eon-ui-linklist
 * @slot image - Holds a single eon-ui-image element
 * @slot content - Holds any number of HTML elements
 */
@Component({
  tag: "eon-ui-card-teaser",
  styleUrls: {
    default: "card-teaser.scss"
  },
  assetsDirs: ["assets-card-teaser"],
  shadow: true
})
export class CardTeaser
  implements
    ColorableComponent<CardTeaserSchemeConfiguration<CardTeaserSchemeDefinition>>,
    ContainerBreakpointComponent {
  schemeConfig: CardTeaserSchemeConfiguration<CardTeaserSchemeDefinition> = CARD_TEASER_SCHEME_CONFIG;
  schemeMap = CARD_TEASER_SCHEME_MAP;
  // private headlineElement: HTMLEonUiHeadlineElement;
  private backgroundImage;
  private contentImage;
  private buttonElements: HTMLCollectionOf<Element>;
  private contentLayer;
  private hasViolator: boolean;
  private hasItemsInSlotDefault: boolean;
  private hasItemsInSlotContent: boolean;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [600];

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: CardTeaserSchemeDefinition;
  @State() seoHeadlineTag: string;

  /**
   * Raised when the Card Teaser has been loaded.
   */
  @Event() cardTeaserLoad: EventEmitter;

  /**
   * Raised when the CTA in Card Teaser has been clicked.
   */
  @Event() cardTeaserClick: EventEmitter;

  @Element() hostElement: HTMLEonUiCardTeaserElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: CardTeaserSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Enable background offset
   */
  @Prop() backgroundOffset: boolean;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * The text of category
   */
  @Prop() category: string;

  /**
   * Defines the position of violator on Y
   */
  // prettier-ignore
  @Prop({ attribute: "violator-position-y" }) violatorVerticalPosition: CardTeaserViolatorVerticalPosition = CARD_TEASER_DEFAULT_VIOLATOR_VERTICAL_POSITION;

  /**
   * Defines the position of violator on X
   */
  // prettier-ignore
  @Prop({ attribute: "violator-position-x" }) violatorHorizontalPosition: CardTeaserViolatorHorizontalPosition = CARD_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Set custom headline color
   */
  @Prop() headlineColor: FlexHeadlineColor;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   *   the HTML-Tag for the info (subheadline) is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    name(Headline) is H2, then info(subheadline) is H3, name(Headline) is H4, then info(subheadline) is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Text alignment
   */
  @Prop() textAlignment: CardTeaserTextAlignment = CARD_TEASER_TEXT_POSITION[0];

  /**
   * Is promo-flag available or not
   */
  @Prop({ reflect: true, mutable: true }) hasPromoFlag: boolean;

  /**
   * Alignment of promoflag if exists
   */
  @Prop() promoFlagAlignment: CardTeaserPromoFlagAlignment = CARD_TEASER_DEFAULT_PROMO_FLAG_ALIGNMENT;

  /**
   * PromoFlag small or normal if exists
   */
  @Prop({ reflect: true, mutable: true }) promoFlagSmall: boolean;

  componentWillLoad() {
    this.hasViolator = !!this.hostElement.querySelector('[slot="violator"]');
    this.hasItemsInSlotDefault = !!this.hostElement.querySelector("*:not([slot])");
    this.hasItemsInSlotContent = !!this.hostElement.querySelector('[slot="content"]');
    this.buttonElements = this.hostElement.getElementsByTagName("EON-UI-BUTTON");
    this.setButtonAttributes();

    const promoFlag = getFirstNestedChild(this.hostElement, "eon-ui-promo-flag") as HTMLEonUiPromoFlagElement;
    this.hasPromoFlag = !!promoFlag;
    this.promoFlagAlignment = promoFlag?.alignment;
    this.promoFlagSmall = promoFlag?.small;

    this.backgroundImage = this.hostElement.querySelector('[slot="image"]') as HTMLEonUiImageElement;
    if (this.backgroundImage) {
      this.backgroundImage.useAsBackground = true;
      this.backgroundImage.positionX = "left";
      this.backgroundImage.positionY = "center";
    }

    this.contentImage = this.hostElement.querySelector('[slot="contentImage"]') as HTMLEonUiImageElement;
    if (this.contentImage) {
      this.contentImage.useAsBackground = true;
      this.contentImage.positionX = "left";
      this.contentImage.positionY = "center";
    }

    this.applyScheme();
    this.handleHeadlineTag();
    this.initContainerBreakpoints();
  }

  componentDidLoad() {
    this.setCssVariables();
    if (this.hasPromoFlag) this.initPromoFlag();
    this.cardTeaserLoad.emit();
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H5);
  }

  @Watch("textAlignment")
  alignText() {
    this.contentLayer.classList.add("text-right");
  }

  private setButtonAttributes() {
    Array.from(this.buttonElements).forEach((button) => {
      if (!button.hasAttribute("full-width-on-mobile")) {
        (button as HTMLEonUiButtonElement).fullWidthOnMobile = true;
      }
    });
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.cardTeaserClick.emit(event);
  }

  initPromoFlag() {
    const isInitialized =
      this.hostElement.hasAttribute("promo-flag-lg") || this.hostElement.hasAttribute("promo-flag-sm");

    if (isInitialized) {
      return;
    }

    const siblingTeasers = Array.from(
      getNestedChildren(this.hostElement.closest("eon-ui-grid-control"), "eon-ui-card-teaser")
    );
    let promoFlagLarge: boolean = false;

    siblingTeasers.forEach((elm) => {
      if (promoFlagLarge) {
        return;
      }

      if (elm.hasAttribute("has-promo-flag")) {
        promoFlagLarge = elm.hasAttribute("promo-flag-small") ? false : true;
      }
    });

    siblingTeasers.forEach((elm) => {
      const attribute = promoFlagLarge ? "promo-flag-lg" : "promo-flag-sm";
      elm.setAttribute(attribute, "true");
    });
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "card-teaser-wrapper": true,
            "no-background": !this.backgroundImage,
            offset: this.backgroundOffset
          })}
        >
          {this.hasPromoFlag && (
            <div
              class={pfx({
                "promo-flag": true,
                [this.promoFlagAlignment]: true
              })}
            >
              <slot name="promo-flag" />
            </div>
          )}
          <div class={pfx({ "card-teaser-wrapper-inner": true })}>
            <div class={pfx({ "image-layer": true })}>
              <div
                class={pfx({
                  "image-layer-background-image": true,
                  "colored-background": !this.backgroundOffset
                })}
              >
                <slot name="image"></slot>
              </div>
              <div class={pfx("image-layer-content-image")}>
                {this.hasViolator ? (
                  <div
                    class={pfx({
                      "card-teaser-violator": true,
                      [`position-y-${this.violatorVerticalPosition}`]: !!this.violatorVerticalPosition,
                      [`position-x-${this.violatorHorizontalPosition}`]: !!this.violatorHorizontalPosition
                    })}
                  >
                    <slot name="violator"></slot>
                  </div>
                ) : (
                  ""
                )}
                <slot name="contentImage"></slot>
              </div>
            </div>
            <div class={pfx("content-layer")} ref={(el) => (this.contentLayer = el as HTMLElement)}>
              <div class={pfx({ ["content-layer-spacer"]: true })}></div>
              <div
                class={pfx({
                  ["content-layer-wrapper"]: true,
                  [`text-${this.textAlignment}`]: true
                })}
              >
                {!!this.category && <eon-ui-text class={pfx("card-teaser-category")}>{this.category}</eon-ui-text>}
                {this.headlineText && (
                  <div class={pfx({ "card-teaser-headline": true, [`${this.headlineColor}`]: !!this.headlineColor })}>
                    <eon-ui-headline
                      size={HEADLINE_SIZE_H5}
                      headline-html-tag={this.seoHeadlineTag}
                      text={this.headlineText}
                    ></eon-ui-headline>
                  </div>
                )}
                <div class={pfx({ "card-teaser-content": true, "has-content": this.hasItemsInSlotContent })}>
                  <slot name="content"></slot>
                </div>
                <div class={pfx({ "card-teaser-links": true, "has-content": this.hasItemsInSlotDefault })}>
                  <slot></slot>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
